import moment from 'moment-timezone'
import constants from './constants'

// Normalize a naive date/time string from the server so it is unambiguous.
// Server sends times already in Europe/Athens without a timezone suffix
// (e.g. "2026-05-26T18:10:00"). moment.tz() treats the string as Athens local
// time and returns a correct UTC-anchored Date, regardless of client timezone.
export const normalizeToAthens = (date) => {
  if (date instanceof Date) return date
  if (typeof date === 'string') {
    if (date.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(date)) {
      return new Date(date)
    }
    return moment.tz(date, constants.timezone).toDate()
  }
  return new Date(date)
}

export const todayString = {
  en: 'TODAY',
  el: 'ΣΗΜΕΡΑ',
}

export const getDateDay = (date) => {
  return normalizeToAthens(date).toLocaleString('en-US', {
    day: 'numeric',
    timeZone: constants.timezone,
  })
}

export const getDayName = (date, local) => {
  const daysEnglish = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ]
  const daysGreek = [
    'Κυριακή',
    'Δευτέρα',
    'Τρίτη',
    'Τετάρτη',
    'Πέμπτη',
    'Παρασκευή',
    'Σάββατο',
  ]
  const dayName = normalizeToAthens(date).toLocaleString('en-US', {
    weekday: 'long',
    timeZone: constants.timezone,
  })
  const dayIndex = daysEnglish.indexOf(dayName)
  return local.toLowerCase().includes('en')
    ? daysEnglish[dayIndex]
    : daysGreek[dayIndex]
}

export const getMonthName = (date, local) => {
  const monthEnglish = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]
  const monthGreek = [
    'Ιανουάριος',
    'Φεβρουάριος',
    'Μάρτιος',
    'Απρίλιος',
    'Μάιος',
    'Ιούνιος',
    'Ιούλιος',
    'Αύγουστος',
    'Σεπτέμβριος',
    'Οκτώβριος',
    'Νοέμβριος',
    'Δεκέμβριος',
  ]
  const monthName = normalizeToAthens(date).toLocaleString('en-US', {
    month: 'long',
    timeZone: constants.timezone,
  })
  const monthIndex = monthEnglish.indexOf(monthName)
  return local.toLowerCase().includes('en')
    ? monthEnglish[monthIndex]
    : monthGreek[monthIndex]
}

export const getMonthShortName = (date, local) => {
  const d = normalizeToAthens(date)
  return local.toLowerCase().includes('en')
    ? d.toLocaleString('en-US', {
        month: 'short',
        timeZone: constants.timezone,
      })
    : d.toLocaleString('el-GR', {
        month: 'short',
        timeZone: constants.timezone,
      })
}

export const getDayShortName = (date, local) => {
  const d = normalizeToAthens(date)
  return local.toLowerCase().includes('en')
    ? d.toLocaleString('en-US', {
        weekday: 'short',
        timeZone: constants.timezone,
      })
    : d.toLocaleString('el-GR', {
        weekday: 'short',
        timeZone: constants.timezone,
      })
}

export const getLocaleDateString = (date) => {
  return normalizeToAthens(date).toLocaleDateString('el-GR', {
    timeZone: constants.timezone,
  })
}

export const getFormattedDate = (date, local, formatOptions) => {
  const { lang } = document.documentElement
  const locale = (local || lang).includes('en') ? 'en-US' : 'el-GR'
  return normalizeToAthens(date).toLocaleDateString(locale, {
    ...formatOptions,
    timeZone: constants.timezone,
  })
}

export const getFormattedDateWithSeparators = (
  date,
  local,
  formatOptions,
  separator = '/',
) => {
  const dateValue = normalizeToAthens(date).toLocaleDateString(local, {
    ...formatOptions,
    timeZone: constants.timezone,
  })
  return dateValue.split('/').join(separator)
}

export const extractFullTimeFromDate = (date) => {
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: constants.timezone,
  }).format(normalizeToAthens(date))
}

export const getCurrentYear = () => {
  return parseInt(moment.tz(new Date(), constants.timezone).format('YYYY'))
}

export const getTodayLocalString = (local) => {
  return todayString[local]
}

export const sortDates = (dates) => {
  return [...dates].sort((a, b) => normalizeToAthens(a) - normalizeToAthens(b))
}

export const toISODateString = (date) => {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: constants.timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(normalizeToAthens(date))
}
